<template>
  <div class="loading-content">
    <div class="text-center">
      <Loader v-if="loading" class="mx-auto"/>
      <div class="text-dark">{{ message }}</div>
      <div class="text-danger">{{ error }}</div>
    </div>
    <form
      v-if="!loading && authInputRequired && !authDataRequired && !bankIdLoginRequired && !cookiesDisabled"
      v-on:submit.prevent="submitCredentials"
    >
      <div
        class="form-inline justify-content-center mb-5"
        v-if="authOptions.length > 1"
      >
        <span class="mr-2">{{ $t('Authentication method') }}</span>
        <select
          class="form-control"
          v-model="authMethod"
        >
          <option
            v-for="opt in authOptions"
            :value="opt.method"
            :key="`auth-method-${opt.method.replace(/\s+/g, '-')}`"
          >{{ opt.title || opt.method }}
          </option>
        </select>
      </div>
      <div
        class="form mb-5"
        v-if="authCredentials && authCredentials.length"
      >
        <div
          class="form-group row justify-content-center"
          v-for="cred in authCredentials"
          :key="`auth-credential-${cred.name}`"
        >
          <div class="col-sm-6 col-lg-4">
            <div class="input-group">
              <input
                class="form-control"
                :disabled="prefilled"
                :name="`credential-${cred.name}`"
                :required="cred.required"
                :placeholder="`${ $t(cred.title || cred.name) }`"
                :type="cred.visible ? 'text' : 'password'"
                :pattern="cred.template"
                v-model="cred.value"
              >
              <div class="input-group-append">
                <span
                  class="input-group-text px-2 py-1"
                  role="button"
                  v-on:click="$set(cred, 'visible', !cred.visible)"
                ><i class="icon" :class="cred.visible ? 'eye' : 'eye-slash'"></i></span>
              </div>
            </div>
            <small>{{ $t(cred.description) }}</small>
          </div>
        </div>
      </div>
      <div class="form-inline justify-content-center">
        <button
          type="submit"
          class="btn btn-theme"
          :disabled="!authCredentialsSet"
        >{{ $t('Authenticate') }}
        </button>
      </div>
    </form>
    <div v-if="!loading && !bankIdLoginRequired  && !paymentAccountSelectionRequired">
      <div v-if="authData.type == 'MESSAGE'">
        <p class="text-center"><strong>{{ authData.value }}</strong></p>
      </div>
      <div v-if="authData.type == 'OTP_CODE' && authDataRequired">
        <p class="text-center"><strong>{{ authData.value }}</strong></p>
        <div class="form-inline justify-content-center">
          <input type="text" name="otpCode" class="form-control" required :placeholder="$t('OTP code')" v-model="otpCode">
          <button class="btn btn-theme" v-on:click="submitOtpCode">{{ $t('Submit') }}</button>
        </div>
      </div>
    </div>
    <div v-if="!loading && userConsentRequired">
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-md-8 col-lg-6">
          <div v-if="service === 'AIS'">
            <p class="text-justify">
              <i18n tag="span" path="Authentication is initiated by {0}." class="mr-1"><strong>{{ appName }}</strong>
              </i18n>
              <i18n tag="span"
                    path="After you complete authentication, {0} will be shared with the authentication initiator."></i18n>
              <i18n tag="span" path="By pressing {0} button you agree with the {1}." class="mr-1">
                <span>"{{ $t('Continue authentication') }}"</span><a href="/terms"
                                                                     target="_blank">{{ $t('terms of service') }}</a>
              </i18n>
              <i18n tag="span" path="Press {0} button if you are not willing to share your payment accounts data.">
                <span>"{{ $t('Cancel') }}"</span></i18n>
            </p>
          </div>
          <div v-else-if="service === 'PIS'">
            <p>
              <i18n tag="strong" path="Payment details"></i18n>
            </p>
            <i18n tag="p" path="Payment ID provided by the application: {0}">
              <strong>{{ paymentInfo.endToEndIdentification }}</strong></i18n>
            <i18n tag="p" path="Account number where to transfer funds: {0}">
              <strong>{{ paymentInfo.creditorAccount.identification }}</strong></i18n>
            <i18n tag="p" path="Payee name in the payment: {0}"><strong>{{ paymentInfo.creditorName }}</strong></i18n>
            <i18n tag="p" path="Payee country: {0}"
                  v-if="paymentInfo.creditorAddress && paymentInfo.creditorAddress.country"><strong>{{
                countryNames[paymentInfo.creditorAddress.country] || paymentInfo.creditorAddress.country
              }}</strong></i18n>
            <i18n tag="p" path="Payment amount: {0}"><strong>{{ paymentInfo.amount }} {{
                paymentInfo.currency
              }}</strong></i18n>
            <i18n tag="p" path="Message or reference number in the payment: {0}">
              <strong>{{ paymentInfo.remittanceInformation }}</strong></i18n>
            <p class="text-justify">
              <i18n tag="span" path="Payment is initiated by {0}." class="mr-1"><strong>{{ appName }}</strong></i18n>
              <i18n tag="span"
                    path="After you complete authentication, your payment consent will be requested by your account servicing payment service provider."
                    class="mr-1"></i18n>
              <i18n tag="span" path="By pressing {0} button you agree with the {1}." class="mr-1">
                <span>"{{ $t('Continue authentication') }}"</span><a href="/terms"
                                                                     target="_blank">{{ $t('terms of service') }}</a>
              </i18n>
              <i18n tag="span" path="Press {0} button if you are not willing to complete this payment.">
                <span>"{{ $t('Cancel') }}"</span></i18n>
            </p>
          </div>
          <div class="form-inline justify-content-center">
            <button class="btn btn-theme" v-on:click="submitDataSharingConsent">{{
                $t('Continue authentication')
              }}
            </button>
          </div>
          <div class="text-center">
            <button class="btn btn-link" :disabled="cancelDisabled" v-on:click="cancelAuth">{{ $t("Cancel") }}</button>
          </div>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 small text-muted">
          <p><strong>{{ $t('About {appName}', {appName: appName}) }}</strong></p>
          <p class="text-justify">{{ appDescription }}</p>
          <!--
          <p><strong>About this service</strong></p>
          <p class="text-justify">TBD</p>
          -->
        </div>
      </div>
    </div>
    <bank-id v-if="!loading && bankIdAppUri" :bankIdAppUri="bankIdAppUri" :displayOnly="displayOnly"
          v-on:BankIdAppRedirect="openBankIdApp" v-on:BankIdResume="onBankIdResume" v-on:PageHidden="stopUpdate" />
    <div v-if="!loading && paymentAccountSelectionRequired">
      <h1 class="text-center">{{ $t('Select Payment Account') }}</h1>
      <div class="text-center">
        <div v-for="(paymentAccount, index) in paymentAccounts.accounts">
          <div class="form-check">
            <input class="form-check-input" type="radio" name="exampleRadios" id="exampleRadios1" v-bind:value=index
                   v-model="selectedAccountPosition">
            <label class="form-check-label" for="exampleRadios1">
              {{ paymentAccount.identifications[0].schemeName == null ? "Other" : paymentAccount.identifications[0].schemeName }}:
              {{ paymentAccount.identifications[0].identification }};<br/>
              {{ paymentAccount.details == null ? "" : paymentAccount.details }}
              {{ paymentAccount.product == null ? "" : paymentAccount.product }}
            </label>
          </div>
        </div>
      </div>
      <div class="form-inline justify-content-center">
        <button class="btn btn-theme" v-on:click="submitPaymentAccountSelection">{{ $t('Confirm') }}</button>
      </div>
    </div>
    <div class="text-center" v-if="!userConsentRequired && !loading && !cookiesDisabled">
      <button class="btn btn-link" :disabled="cancelDisabled" v-on:click="cancelAuth">{{ $t("Cancel") }}</button>
    </div>
  </div>
</template>

<script>
import {i18n} from '../i18n.js'
import config from '../config.js'
import constants from '../constants.js'
import Loader from '../components/elements/Loader.vue'
import BaseService from '../services/BaseService.js'
import QRCode from 'qrcode'
import BankId from '../components/BankId.vue'


export default {
  name: 'home',
  components: {
    Loader,
    BankId
  },
  data() {
    return {
      countryNames: constants.countriesNames,
      error: '',
      loading: true,
      cancelDisabled: false,
      message: '',
      service: null,
      country: null,
      prevStatus: null,
      status: null,
      aspspName: '',
      authMethod: '', // authentication method selected by a user
      authOptions: [],
      authData: '',
      authDataRequired: false,
      otpCode: '',
      appName: '',
      appDescription: '',
      accessScope: 'your payment accounts data',
      paymentInfo: null,
      userConsentRequired: false,
      bankIdAppUri: '',
      bankIdLoginRequired: false,
      bankIdResumeRequired: false,
      bankIdResumed: false,
      displayOnly: null,
      timeoutId: 0,
      redirectTimeoutId: 0,
      requestsCount: 0,
      paymentAccountSelectionRequired: false,
      paymentAccounts: '',
      selectedAccountPosition: '',
      sessionRestored: false,
      cookiesDisabled: false,
      _prefilled: null
    }
  },
  computed: {
    authInputRequired: function () {
      return (this.authOptions.length > 1 || (
        this.authOptions.length === 1 &&
        this.authOptions[0].info &&
        this.authOptions[0].info.credentials &&
        this.authOptions[0].info.credentials.length
      )) || !!this.error
    },
    authOption: function () {
      return this.authOptions.find(opt => (opt.method || '') === this.authMethod)
    },
    authCredentials: function () {
      return (this.authOption && this.authOption.info && this.authOption.info.credentials) || []
    },
    authCredentialsSet: function () {
      for (const cred of this.authCredentials) {
        if (cred.required && !cred.value) {
          return false
        }
      }
      return true
    },
    appDescriptionLocal: function () {
      if (typeof this.appDescription === 'string' && this.appDescription.length > 0) {
        return this.appDescription
      } else if (
        this.appDescription &&
        typeof this.appDescription[this.$i18n.locale] === 'string' &&
        this.appDescription[this.$i18n.locale].length > 0
      ) {
        return this.appDescription[this.$i18n.locale]
      } else {
        return this.$t('Application description is not provided.')
      }
    }
  },
  authOption: function () {
    return this.authOptions.find(opt => (opt.method || '') === this.authMethod)
  },
  authCredentials: function () {
    return (this.authOption && this.authOption.info && this.authOption.info.credentials) || []
  },
  authCredentialsSet: function () {
    for (const cred of this.authCredentials) {
      if (cred.required && !cred.value) {
        return false
      }
    }
    return true
  },
  prefilled: function () {
    // evaluated only once
    if (this._prefilled === null || this._prefilled === undefined) {
      this._prefilled = !!this.authCredentialsSet
    }
    return this._prefilled
  },
  methods: {
    async handleWaitingConsentConfirmation(data) {
      this.appName = data.applicationName
      this.appDescription = data.applicationDescription
      this.userConsentRequired = true
      this.paymentInfo = data.paymentInfo
      if (data.access) { // todo handle this on backend
        if (data.access.balances && data.access.transactions) {
          this.accessScope = 'the list of your payment accounts, accounts balances and transactions'
        } else if (data.access.balances) {
          this.accessScope = 'the list of your payment accounts and accounts balances'
        } else if (data.access.transactions) {
          this.accessScope = 'the list of your payment accounts and accounts transactions'
        } else {
          this.accessScope = 'the list of your payment accounts'
        }
      }
      this.loading = false
    },
    async handleWaitingAccountSelection(data) {
      this.message = '';
      this.paymentAccountSelectionRequired = true;
      this.paymentAccounts = data.accounts;
      this.loading = false
    },
    async handleNotReady(data) {
      this.aspspName = data.aspspName
      this.authOptions = data.authInfo
      this.authMethod = (this.authOptions[0] && this.authOptions[0].method) || ''
      this.bankIdAppUri = ''
      this.bankIdLoginRequired = false
      this.bankIdResumeRequired = false
      this.bankIdResumed = false
      if (this.authInputRequired) {
        this.loading = false
      } else {
        this.message = i18n.t('Starting authorization')
        const r = await BaseService.startAuthorization(null, null, this.$i18n.locale)
        console.log(r)
        this.scheduleUpdate(1000)
      }
    },
    async handleInvalid(data) {
      this.error = i18n.t('Unable to authenticate')
      if (data.redirectUri) {
        this.message = i18n.t('Returning to the authentication initiator')
        this.redirect(data.redirectUri)
      } else {
        this.message = i18n.t('You need to close this window')
      }
    },
    async handleCancelled(data) {
      this.error = i18n.t('Authentication cancelled')
      if (data.redirectUri) {
        this.message = i18n.t('Returning to the authentication initiator')
        this.redirect(data.redirectUri)
      } else {
        this.message = i18n.t('You need to close this window')
      }
    },
    async handleWaitingAspspReturn(data) {
      this.message = ''
      try {
        const r = await BaseService.continueAuthorization({
          redirectUri: window.location.href,
          locale: this.$i18n.locale
        })
        console.log(r)
        window.history.replaceState({}, "", "/")
      } catch (e) {
        throw new Error(i18n.t('Unable to continue authorization'))
      } finally {
        this.scheduleUpdate(500)
      }
    },
    async handleReturnedFromAspsp(data) {
      // continue authorization is already started. Just wait
      this.scheduleUpdate(500)
    },
    async handleAspspRedirect(data) {
      this.message = i18n.t('Redirecting to your account servicing provider')
      const r = await BaseService.notifyLeaving()
      if (r.data.message !== 'OK') {
        throw new Error(r.data.message)
      }
      window.localStorage.setItem('last-locale', this.$i18n.locale)
      this.redirect(data.redirectUri)
    },
    handleBlockingAuthData(data) { // old format
      this.authDataRequired = true;
      this.otpCode = ''
      this.loading = false
    },
    async handleAuthData(data) { // new format
     this.loading = false
     this.bankIdAppUri = data.redirectUri
     this.displayOnly = this.authData.type
     try {
       const r = await BaseService.continueAuthorization({ locale: this.$i18n.locale })
       console.log(r)
     } catch (e) {
       throw new Error(i18n.t('Unable to continue decoupled authorization'))
     }
     this.scheduleUpdate(1000, this.loading)
    },
    async handleWaitingUserDecoupledAuth(data) {
      this.message = ''
      if (data.authData) {
        this.authData = data.authData
        if (["OTP_CODE"].includes(data.authData.type)) {
          return this.handleBlockingAuthData(data)
        }
        return await this.handleAuthData(data)
      } else {
        if (data.redirectUri) { // QR code generation flow
          this.bankIdAppUri = data.redirectUri
          this.bankIdLoginRequired = true
          this.message = ''
          const url = new URL(this.bankIdAppUri)
          if (this.bankIdResumed) {
            this.loading = true
          } else if (this.bankIdAppUri.startsWith("bankid:///")) {
            this.loading = false
            url.searchParams.delete('redirect')
            this.$nextTick(() => {
              const qrCodeCanvas = document.getElementById("qrCodeCanvas")
              if (qrCodeCanvas === null) {
                // the element is not available
                return
              }
              qrCodeCanvas.style.display = "block"
              QRCode.toCanvas(qrCodeCanvas, url.href, {}, function (err, canvas) {
                if (err) throw err
              })
            })
          } else {
            this.loading = false
            // Image is dynamic and updated every second
            const url = new URL(this.bankIdAppUri)
            url.searchParams.set("ts", (new Date()).getTime())
            this.bankIdAppUri = url.href
            this.$nextTick(() => {
              const qrCodeImage = document.getElementById("qrCodeImage")
              if (qrCodeImage === null) {
                // the element is not available
                return
              }
              qrCodeImage.style.display = "block"
            })
          }
          try {
            const r = await BaseService.continueAuthorization({locale: this.$i18n.locale})
            console.log(r)
          } catch (e) {
            throw new Error(i18n.t('Unable to continue decoupled authorization'))
          }
          this.scheduleUpdate(1000, false)
        } else {
          this.message = i18n.t('To continue please use your authentication app')
          try {
            const r = await BaseService.continueAuthorization({locale: this.$i18n.locale})
            console.log(r)
          } catch (e) {
            throw new Error(i18n.t('Unable to continue decoupled authorization'))
          }
          this.scheduleUpdate(500)
        }
      }
    },
    async handleApplicationRedirect(data) {
      this.message = i18n.t('Returning to the authentication initiator')
      window.localStorage.setItem('last-return-url', data.redirectUri)
      this.redirect(data.redirectUri)
    },
    async handleAuthStarted(data) {
      // just wait
      this.scheduleUpdate(500)
    },
    redirect(redirectUrl) {
      this.loading = true;
      this.redirectTimeoutId = setTimeout(() => {
        window.location.href = redirectUrl
      }, 500)
    },
    async checkNavigationType() {
      const navigationType = (window.performance.getEntriesByType('navigation')[0]).type
      if (navigationType === "back_forward") {
        try {
          const r = await BaseService.cancelAuth();
        } catch (e) {
          console.error(e)
        }
      }
    },
    async updateStatus() {
      try {
        this.timeoutId = 0
        const r = await BaseService.getSession()
        this.sessionRestored = false
        this.error = ''
        this.prevStatus = this.status
        this.service = r.data.service
        this.status = r.data.status
        // Checking country and updating locale if necessary
        if (r.data.country && r.data.country !== this.country) {
          this.country = r.data.country
          const urlLang = new URLSearchParams(window.location.search).get('locale')
          const selectedLang = window.localStorage.getItem('selected-language')
          if (config.countryLocales[this.country] &&
            config.countryLocales[this.country] !== this.$i18n.locale &&
            !(urlLang && this.$i18n.messages[urlLang]) &&
            !(selectedLang && this.$i18n.messages[selectedLang]) &&
            this.$i18n.messages[config.countryLocales[this.country]] &&
            (
              !r.data.language ||
              r.data.language.match(
                new RegExp(
                  // Checking if locale is in accepted languages (of it any are accepted)
                  `(^|[^\\w-])(${config.countryLocales[this.country]}|\\*)(\\W|$)`, 'i'
                )
              )
            )) {
            this.$root.$i18n.locale = config.countryLocales[this.country]
          }
        }
        const handlerName = this.status
          .split('_')
          .map(e => e.substr(0, 1) + e.substr(1).toLowerCase())
          .join('')
        const handler = this[`handle${handlerName}`]
        if (!handler) {
          this.message = ''
          throw new Error(`Unable to handle authentication ${this.status}`)
        }
        handler(r.data)
      } catch (e) {
        let retry = true
        if (e.response) {
          if (e.response.status === 404) {
            this.error = i18n.t('Unable to access server')
            retry = false
          } else if (e.response.data && e.response.data.error === 'PSU_SESSION_COOKIE_MISSING' && this.sessionRestored) {
            this.showCookiesDisabled()
            retry = false
          } else if (e.response.status === 422) { // was changed, check this when migrating to tilisy ui
            try {
              await BaseService.restoreSession(window.location.href)
              this.sessionRestored = true
            } catch (e) {
              let lastReturnUrl = window.localStorage.getItem('last-return-url')
              if (lastReturnUrl != null) {
                this.message = i18n.t('Returning to the authentication initiator')
                this.redirect(lastReturnUrl)
              } else {
                this.error = i18n.t('Authentication session has expired')
              }
            }
          } else {
            this.error = i18n.t('Error while updating authentication status')
          }
        } else if (e.message) {
          this.error = e.message
        } else {
          this.error = i18n.t('Unknown error occurred')
        }
        this.loading = retry
        if (retry) {
          this.scheduleUpdate(500)
        }
      } finally {
        this.requestsCount++
      }
    },
    showCookiesDisabled() {
      this.cookiesDisabled = true
      this.message = ''
      this.error = i18n.t('Cookies must be enabled in your browser to continue')
    },
    scheduleUpdate(timeout, loading = true) {
      if (this.timeoutId !== 0) {
        // We don't want to schedule new update if there is already update scheduled
        return
      }
      this.loading = loading
      if (this.requestsCount > 10) {
        timeout = timeout * Math.ceil(this.requestsCount / 10)
      }
      this.timeoutId = setTimeout(this.updateStatus, timeout)
    },
    async submitCredentials() {
      this.loading = true
      this.error = ''
      try {
        const r = await BaseService.startAuthorization(
          this.authMethod,
          Object.fromEntries(this.authCredentials.map(c => [c.name, c.value])),
          this.$i18n.locale
        )
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    },
    openBankIdApp() {
      clearTimeout(this.timeoutId)
      this.timeoutId = 0
      this.bankIdResumeRequired = true
      this.bankIdResumed = false
      const url = new URL(this.bankIdAppUri)
      const userAgent = navigator.userAgent
      let redirect = null
      // if iOS device
      if (/iphone|ipod|ipad/i.test(userAgent)) {
        if (/fxios/i.test(userAgent)) { // Firefox
          redirect = `firefox://open-url?url=${encodeURIComponent(window.location.href)}`
        } else if (/crios/i.test(userAgent)) { // Chrome
          redirect = window.location.protocol.replace(/^http/, 'googlechrome') + '//'
        } else if (/version\//i.test(userAgent)) { // Probably Safari
          if (url.searchParams.get('redirect')) {
            window.history.replaceState({}, null, url.searchParams.get('redirect'))
          }
          redirect = window.location.href
        }
      }
      url.searchParams.set('redirect', redirect)
      window.location.href = url.href
    },
    stopUpdate() {
      window.stop()
      clearTimeout(this.timeoutId)
      this.timeoutId = 0
    },
    async onBankIdResume() {
      this.bankIdResumed = true
      this.bankIdResumeRequired = false
      this.bankIdLoginRequired = false
      this.loading = true
      await BaseService.continueAuthorization({locale: this.$i18n.locale})
      this.scheduleUpdate(1000)
    },
    async submitOtpCode() {
      this.loading = true
      try {
        const r = await BaseService.continueAuthorization({code: this.otpCode, locale: this.$i18n.locale})
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    },
    async submitPaymentAccountSelection() {
      this.loading = true
      try {
        const r = await BaseService.submitPaymentAccountSelection({accountPosition: parseInt(this.selectedAccountPosition)})
        this.paymentAccountSelectionRequired = false;
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    },
    async submitDataSharingConsent() {
      this.loading = true
      try {
        const r = await BaseService.confirmConsent()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.userConsentRequired = false
      this.scheduleUpdate(500)
    },
    async cancelAuth() {
      clearTimeout(this.timeoutId)
      this.timeoutId = 0
      if (this.redirectTimeoutId !== 0) {
        clearTimeout(this.redirectTimeoutId)
        this.redirectTimeoutId = 0
      }
      window.stop()
      this.loading = true
      this.cancelDisabled = true
      try {
        const r = await BaseService.cancelAuth()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    }
  },
  mounted() {
    if (!navigator.cookieEnabled) {
      this.showCookiesDisabled()
      this.loading = false
      return
    }
    this.checkNavigationType()
    this.updateStatus()
  }
}
</script>

<style lang="scss" scoped>
.loading-content {
  padding-top: 20vh;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode-image {
  border: 1px solid #ccc;
}

.icon {
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  width: 1rem;
  height: 1rem;
}

.eye {
  background-image: url(../assets/eye.svg);
}

.eye-slash {
  background-image: url(../assets/eye-slash.svg);
}
</style>
